import { useEffect } from 'react';
import { twMerge } from 'tailwind-merge';
import {
  useActiveAccount,
  useActiveAccountCreditBalance,
  useDispatch,
} from '@witmetrics/data-store';
import { DIALOGS } from '@witmetrics/unison-core';
import { useAppState } from '@/providers/AppStateProvider';
import Tooltip from '@/components/Tooltip';
import { CreditsIcon } from '@/icons';
import {
  classes,
  fetchLabBalance,
  fetchPracticeBalance,
  formatCredits,
} from './utils';

export type CreditsBalanceProps = {
  className?: string;
};

export default function CreditsBalance({ className }: CreditsBalanceProps) {
  const dispatch = useDispatch();
  const { onApiError, onOpenDialog } = useAppState();
  const activeAccount = useActiveAccount();
  const balance = useActiveAccountCreditBalance();

  useEffect(() => {
    if (activeAccount) fetchData();
  }, [activeAccount?.accountId, activeAccount?.accountType]);

  const fetchData = async () => {
    if (!activeAccount) return;
    try {
      if (activeAccount.accountType === 'PRACTICE') {
        const balance = await fetchPracticeBalance(activeAccount.accountId);
        dispatch.setPracticeCreditBalance({
          practiceId: activeAccount.accountId,
          balance,
        });
      } else {
        const balance = await fetchLabBalance(activeAccount.accountId);
        dispatch.setLabCreditBalance({
          labId: activeAccount.accountId,
          balance,
        });
      }
    } catch (err) {
      onApiError(err, 'Error fetching credits', () => fetchData());
    }
  };

  const handleOpen = () => onOpenDialog(DIALOGS.CREDITS);

  if (!activeAccount) return null;

  return (
    <Tooltip arrow enterDelay={0} placement="bottom" title="Credits">
      <button
        className={twMerge(classes.button, className)}
        aria-label="credits"
        onClick={handleOpen}>
        <CreditsIcon className={classes.icon} />
        <span className={classes.balance}>{formatCredits(balance)}</span>
      </button>
    </Tooltip>
  );
}
